/*eslint-disable*/
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

// ==================== VIEW POPUP (READ ONLY) ====================
const ApiViewPopup = ({ selectedApiDocId, setSelectedApiDocId = () => {} }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [doc, setDoc] = useState(null);

  // ─────────────────────────────────────────────
  // HTTP method badge color
  // ─────────────────────────────────────────────
  const methodColorMap = {
    GET: "success",
    POST: "primary",
    PUT: "warning",
    PATCH: "warning",
    DELETE: "danger",
  };

  // ─────────────────────────────────────────────
  // Reset state on close
  // ─────────────────────────────────────────────
  const resetView = () => {
    setDoc(null);
    setFetchError(null);
    setSelectedApiDocId(null);
  };

  // ─────────────────────────────────────────────
  // Fetch API doc details by ID
  // ─────────────────────────────────────────────
  const getApiDocDetails = async () => {
    if (!selectedApiDocId) return;

    try {
      setLoading(true);
      setFetchError(null);

      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_API_DOC_DETAILS +
        `/${selectedApiDocId}`;

      const response = await getData(requestUrl);

      if (response.status) {
        setDoc(response.data);
      } else {
        setFetchError(response.message || "Failed to fetch API doc details.");
      }
    } catch (error) {
      setFetchError("Something went wrong while fetching details.");
      console.error("[ApiViewPopup] getApiDocDetails error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Load details when selectedApiDocId changes
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (selectedApiDocId) {
      getApiDocDetails();
    } else {
      resetView();
    }
  }, [selectedApiDocId]);

  // ─────────────────────────────────────────────
  // Helper — render a parameter row
  // ─────────────────────────────────────────────
  const renderParamRow = (param, index) => (
    <div key={index} className="card mb-2 border">
      <div className="card-body p-3">
        <div className="row g-2 align-items-start">
          <div className="col-md-4">
            <span className="small text-muted d-block">{t("Field Name")}</span>
            <code className="fw-semibold">{param.fieldname || "—"}</code>
          </div>
          <div className="col-md-3">
            <span className="small text-muted d-block">{t("Type")}</span>
            <span className="badge bg-secondary">
              {param.fieldtype || "String"}
            </span>
          </div>
          <div className="col-md-2">
            <span className="small text-muted d-block">{t("Required")}</span>
            {param.required ? (
              <span className="badge bg-danger">{t("Yes")}</span>
            ) : (
              <span className="badge bg-light text-dark border">{t("No")}</span>
            )}
          </div>
          <div className="col-12">
            <span className="small text-muted d-block">{t("Description")}</span>
            <span className="small">
              {param.description || <em className="text-muted">—</em>}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────
  // Helper — render an error code row
  // ─────────────────────────────────────────────
  const renderErrorCodeRow = (err, index) => (
    <div key={index} className="card mb-2 border">
      <div className="card-body p-3">
        <div className="row g-2">
          <div className="col-md-3">
            <span className="small text-muted d-block">{t("Error Code")}</span>
            <span className="badge bg-danger fs-6">{err.code || "—"}</span>
          </div>
          <div className="col-md-9">
            <span className="small text-muted d-block">{t("Description")}</span>
            <span className="small">{err.description || "—"}</span>
          </div>
          {err.response && (
            <div className="col-12">
              <span className="small text-muted d-block">
                {t("Response JSON")}
              </span>
              <pre
                className="bg-light border rounded p-2 small mb-0"
                style={{
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {err.response}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ==================== RENDER ====================
  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="apiViewPopup"
      aria-labelledby="apiViewPopupLabel"
    >
      {/* ── Header ── */}
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              api
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="apiViewPopupLabel"
            >
              {t("API Documentation Details")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {doc ? doc.title : t("Loading...")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetView}
        ></button>
      </div>

      {/* ── Body ── */}
      <div className="offcanvas-body p-4">
        {loading ? (
          // Loading spinner while fetching details
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : fetchError ? (
          // Error state
          <div className="alert alert-danger" role="alert">
            <span className="material-symbols-outlined me-2">error</span>
            {fetchError}
          </div>
        ) : !doc ? null : (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            {/* ── Basic Information ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">
                {t("Basic Information")}
              </h5>

              {/* Method + Version + Auth + Status badges */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span
                  className={`badge bg-${methodColorMap[doc.method] || "secondary"} fs-6`}
                >
                  {doc.method}
                </span>
                <span className="badge bg-light text-dark border fs-6">
                  v{doc.version || "1.0.0"}
                </span>
                {doc.requiresauth ? (
                  <span className="badge bg-warning text-dark fs-6">
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "14px", verticalAlign: "middle" }}
                    >
                      lock
                    </span>{" "}
                    {t("Auth Required")}
                  </span>
                ) : (
                  <span className="badge bg-light text-dark border fs-6">
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "14px", verticalAlign: "middle" }}
                    >
                      lock_open
                    </span>{" "}
                    {t("No Auth")}
                  </span>
                )}
                {doc.APIstatus ? (
                  <span className="badge bg-success fs-6">{t("Active")}</span>
                ) : (
                  <span className="badge bg-danger fs-6">{t("Inactive")}</span>
                )}
              </div>

              {/* Title */}
              <div className="mb-3">
                <span className="small text-muted d-block mb-1">
                  {t("API Title")}
                </span>
                <p className="fw-semibold mb-0">{doc.title}</p>
              </div>

              {/* Endpoint */}
              <div className="mb-3">
                <span className="small text-muted d-block mb-1">
                  {t("Endpoint")}
                </span>
                <code className="bg-light border rounded px-2 py-1 d-inline-block">
                  {doc.endpoint || "—"}
                </code>
              </div>

              {/* Module + Submodule */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <span className="small text-muted d-block mb-1">
                    {t("Module")}
                  </span>
                  <p className="mb-0">
                    {doc.module || <em className="text-muted">—</em>}
                  </p>
                </div>
                <div className="col-md-6">
                  <span className="small text-muted d-block mb-1">
                    {t("Submodule")}
                  </span>
                  <p className="mb-0">
                    {doc.submodule || <em className="text-muted">—</em>}
                  </p>
                </div>
              </div>

              {/* Description */}
              {doc.description && (
                <div className="mb-3">
                  <span className="small text-muted d-block mb-1">
                    {t("Description")}
                  </span>
                  <p className="mb-0">{doc.description}</p>
                </div>
              )}

              {/* Owner + Updated by */}
              <div className="row">
                <div className="col-md-6">
                  <span className="small text-muted d-block mb-1">
                    {t("Owner")}
                  </span>
                  <p className="mb-0">
                    {doc.owner?.name
                      ? `${doc.owner.name} ${doc.owner.surname || ""}`.trim()
                      : doc.owner?.email || <em className="text-muted">—</em>}
                  </p>
                </div>
                <div className="col-md-6">
                  <span className="small text-muted d-block mb-1">
                    {t("Last Updated By")}
                  </span>
                  <p className="mb-0">
                    {doc.updatedby?.name
                      ? `${doc.updatedby.name} ${doc.updatedby.surname || ""}`.trim()
                      : doc.updatedby?.email || (
                          <em className="text-muted">—</em>
                        )}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Query Parameters ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">
                {t("Query Parameters")}
                <span className="badge bg-secondary ms-2">
                  {doc.queryparams?.length || 0}
                </span>
              </h5>
              {!doc.queryparams?.length ? (
                <p className="text-muted small mb-0">
                  {t("No query parameters.")}
                </p>
              ) : (
                doc.queryparams.map((param, index) =>
                  renderParamRow(param, index),
                )
              )}
            </div>

            {/* ── Request Body Parameters ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">
                {t("Request Body Parameters")}
                <span className="badge bg-secondary ms-2">
                  {doc.requestbodyparams?.length || 0}
                </span>
              </h5>
              {!doc.requestbodyparams?.length ? (
                <p className="text-muted small mb-0">
                  {t("No request body parameters.")}
                </p>
              ) : (
                doc.requestbodyparams.map((param, index) =>
                  renderParamRow(param, index),
                )
              )}
            </div>

            {/* ── Success Response ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">
                {t("Success Response")}
              </h5>
              {doc.successresponse ? (
                <pre
                  className="bg-light border rounded p-3 mb-0"
                  style={{
                    fontSize: "12px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {doc.successresponse}
                </pre>
              ) : (
                <p className="text-muted small mb-0">
                  {t("No success response defined.")}
                </p>
              )}
            </div>

            {/* ── Error Codes ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">
                {t("Error Codes")}
                <span className="badge bg-secondary ms-2">
                  {doc.errorcodes?.length || 0}
                </span>
              </h5>
              {!doc.errorcodes?.length ? (
                <p className="text-muted small mb-0">
                  {t("No error codes defined.")}
                </p>
              ) : (
                doc.errorcodes.map((err, index) =>
                  renderErrorCodeRow(err, index),
                )
              )}
            </div>

            {/* ── Meta Info ── */}
            <div className="form-group p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">{t("Meta")}</h5>
              <div className="row">
                <div className="col-md-4">
                  <span className="small text-muted d-block mb-1">
                    {t("API ID")}
                  </span>
                  <span className="fw-semibold">#{doc.customid}</span>
                </div>
                <div className="col-md-4">
                  <span className="small text-muted d-block mb-1">
                    {t("Created At")}
                  </span>
                  <span>
                    {doc.createdAt
                      ? new Date(doc.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </span>
                </div>
                <div className="col-md-4">
                  <span className="small text-muted d-block mb-1">
                    {t("Updated At")}
                  </span>
                  <span>
                    {doc.updatedAt
                      ? new Date(doc.updatedAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Sticky Footer ── */}
      <div
        className="offcanvas-footer p-3 bg-white border-top d-flex justify-content-end"
        style={{ position: "sticky", bottom: 0, zIndex: 10 }}
      >
        <button
          type="button"
          className="btn btn-outline-secondary"
          data-bs-dismiss="offcanvas"
          onClick={resetView}
        >
          {t("Close")}
        </button>
      </div>
    </div>
  );
};

export default ApiViewPopup;
